import { toast } from 'sonner';
import { generateMockTransactions } from '@/data/payments-mock-data';
import { graphqlQuery } from '@/lib/api/graphql';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import { createAllocationsDemoResponse } from '@/services/demo/allocations-demo.service';
import type { CreateAllocationsRequest, CreateAllocationsResponseApi } from '@/types/api/allocations';
import type { Transaction, TransactionStatus, TransactionType } from '@/types/payments';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';
import { useReceiptByIdGraphQL } from './useReceiptsGraphQL';
import {
  TRANSACTION_LIST_QUERY,
  type TransactionGraphQLNode,
  type TransactionListGraphQLResponse,
} from './useTransactionsGraphQL';

// Map GraphQL transaction node to payments Transaction type
function mapGraphQLNodeToPaymentsTransaction(node: TransactionGraphQLNode, homeCurrency: string): Transaction {
  const dueDate = node.dueDate ? new Date(node.dueDate) : new Date();
  let status: TransactionStatus = 'outstanding';
  if (node.outstandingAmount <= 0) {
    status = 'completed';
  } else if (node.daysOverdue > 0) {
    status = 'overdue';
  }

  const isNonHomeCurrency = (node.currencyCode ?? homeCurrency) !== homeCurrency && node.currencyAmount != null;

  return {
    id: node.transactionId,
    transactionNumber: node.referenceNumber ?? String(node.transactionId),
    transactionType: (node.transactionTypeCode ?? 'invoice').toLowerCase() as TransactionType,
    customerId: node.customerId,
    customerName: node.customerName ?? '',
    transactionDate: new Date(node.transactionDate),
    dueDate,
    originalAmount: isNonHomeCurrency ? (node.currencyAmount ?? 0) : node.amount,
    currentBalance: isNonHomeCurrency ? (node.currencyOutstandingAmount ?? 0) : node.outstandingAmount,
    currency: node.currencyCode ?? homeCurrency,
    homeCurrencyAmount: isNonHomeCurrency ? node.amount : undefined,
    homeCurrencyBalance: isNonHomeCurrency ? node.outstandingAmount : undefined,
    status,
    daysOverdue: node.daysOverdue,
    onDispute: node.onDispute,
    hasDispute: node.hasDispute,
    isPromised: node.hasPromise,
    description: node.description ?? undefined,
  };
}

/**
 * Hook to fetch a single receipt by ID for allocation
 * Handles both Demo and Live modes via GraphQL
 */
export function useReceiptForAllocation(receiptId: number | undefined, homeCurrency?: string) {
  return useReceiptByIdGraphQL(receiptId, homeCurrency);
}

/**
 * Hook to fetch transactions for a customer (for allocation)
 * Handles both Demo and Live modes
 */
export function useTransactionsForAllocation(customerId: number | undefined, homeCurrency: string) {
  const apiConfig = useApiConfig();
  const mockTransactions = customerId ? generateMockTransactions(customerId) : [];

  return useModeAwareQuery<Transaction[], Error>(
    ['transactions-for-allocation', customerId],
    async ({ signal }) => {
      if (!customerId) return [];

      const filter: Record<string, unknown> = {};
      filter.customerId = { eq: customerId };
      filter.transactionTypeCode = { notContainsValues: ['receipt'] };
      filter.outstandingAmount = { ne: 0 };
      filter.onDispute = { eq: false };
      filter.hasDispute = { eq: false };

      const response = await graphqlQuery<TransactionListGraphQLResponse>(
        apiConfig,
        TRANSACTION_LIST_QUERY,
        { first: 10000, filter },
        signal,
      );

      const { edges, pageInfo } = response.transactions.transactionList;

      if (pageInfo.hasNextPage) {
        toast.warning('Results truncated', {
          description: 'Over 10,000 transactions found for this customer. Only the first 10,000 rows have been loaded.',
        });
      }

      return edges.map((edge) => mapGraphQLNodeToPaymentsTransaction(edge.node, homeCurrency));
    },
    mockTransactions.filter((t) => t.currentBalance !== 0),
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

/**
 * Hook to create allocations
 * Handles both Demo and Live modes
 */
export function useCreateAllocations() {
  const { apiPost } = useApi();

  return useModeAwareMutation<CreateAllocationsResponseApi, Error, CreateAllocationsRequest>(async (request) => {
    return apiPost<CreateAllocationsResponseApi>('/api/v1/allocations', request);
  }, createAllocationsDemoResponse);
}

/**
 * Combined hook for allocation workspace data
 * Returns receipt, transactions, and mutation for creating allocations
 */
export function useAllocationWorkspaceData(receiptId: number | undefined, homeCurrency: string) {
  const receiptQuery = useReceiptForAllocation(receiptId, homeCurrency);
  const customerId = receiptQuery.data?.customerId;
  const transactionsQuery = useTransactionsForAllocation(customerId, homeCurrency);
  const createAllocationsMutation = useCreateAllocations();

  return {
    receipt: receiptQuery.data,
    transactions: transactionsQuery.data ?? [],
    isLoading: receiptQuery.isLoading || (receiptQuery.data && transactionsQuery.isLoading),
    isLoadingReceipt: receiptQuery.isLoading,
    isLoadingTransactions: transactionsQuery.isLoading,
    error: receiptQuery.error || transactionsQuery.error,
    createAllocations: createAllocationsMutation.mutateAsync,
    isSubmitting: createAllocationsMutation.isPending,
    refetch: () => {
      receiptQuery.refetch();
      transactionsQuery.refetch();
    },
  };
}
